import { computed, reactive, watch } from "vue"
import { createGoogle, GoogleError } from "@auger/google"
import { google as googleConfig } from "@/common/config"
import { httpErrorMessage } from "@/common/fetchclient"
import { netlifyFunctionClient } from "@/common/functions"
import { getAuger } from "@/init/auger"
import { CONNECTED_APP_SCOPES, useGoogleStore } from "@/stores/google"
import { useUserStore } from "@/stores/user"
import { errorMessage } from "@/utils/errors"
import type { ComposableDeps } from "./types"

// --- Google Identity Services on `window` ---
// Only the calls this file makes.

interface CodeResponse {
  code: string
  scope: string
}

interface GoogleAccounts {
  oauth2: {
    initCodeClient(config: {
      client_id: string
      scope: string
      ux_mode: "popup"
      hint: string
      hosted_domain: string
      callback: (response: CodeResponse) => void
      error_callback: (err: unknown) => void
    }): CodeClient
    revoke(token: string): void
  }
}

interface CodeClient {
  requestCode(): void
}

declare global {
  interface Window {
    google: { accounts: GoogleAccounts }
  }
}

export type { GoogleToken } from "@/stores/google"
import type { GoogleToken } from "@/stores/google"

// What the google_token and complete_authorization functions answer with.
interface GoogleTokenResponse {
  token?: GoogleToken
  status?: "authorization_needed"
}
type CompleteAuthorizationResponse = Pick<GoogleTokenResponse, "token">

/** A failed Google call's message as a toast should show it: Google's own when
 *  it sent one. */
export function googleErrorMessage(err: unknown): string {
  return (err instanceof GoogleError && err.messages[0]) || errorMessage(err) || "Unknown error"
}

// --- OAuth scopes ---

export const GMAIL_MODIFY_SCOPE = "https://www.googleapis.com/auth/gmail.modify"

// The scopes the consent popup asks for. CONNECTED_APP_SCOPES add nothing to
// what the person sees on Google's consent screen (calendar and gmail.modify
// cover them); they are listed so a Connected App can get a token for them
// alone.
export const SCOPES = {
  calendar: [
    "https://www.googleapis.com/auth/calendar",
    "https://www.googleapis.com/auth/calendar.events",
  ],
  // gmail.modify reads mail and changes its labels (archive, trash); it can't
  // send mail or delete permanently.
  gmail: [GMAIL_MODIFY_SCOPE],
  connectedApps: CONNECTED_APP_SCOPES,
}

// --- Script loading state (module-level, shared across all instances) ---

const scripts = reactive({
  loadingGIS: false,
  loadedGIS: false,
})

// --- Internal helpers ---

const EXPIRY_BUFFER = 5 * 60 * 1000 // 5 minutes

function isTokenActive(token: GoogleToken | null) {
  if (!token?.token) return false
  const expires = new Date(token.expires_at).getTime()
  return expires > Date.now() + EXPIRY_BUFFER
}

// wait for user store to have an email available
function getUserEmail() {
  return new Promise<string>((resolve) => {
    const userStore = useUserStore()
    if (userStore.email) return resolve(userStore.email)
    watch(
      () => userStore.email,
      (email) => {
        if (email) resolve(email)
      }
    )
  })
}

// wait for google libraries to finish loading
function ensureLibrariesLoaded() {
  return new Promise<GoogleAccounts>((resolve) => {
    const store = useGoogleStore()
    if (store.librariesLoaded) return resolve(window.google.accounts)
    watch(
      () => store.librariesLoaded,
      (loaded) => {
        if (loaded) resolve(window.google.accounts)
      }
    )
  })
}

// wait for full init (libraries + token check) to complete
function ensureReady() {
  return new Promise<void>((resolve) => {
    const store = useGoogleStore()
    if (store.ready) return resolve()
    watch(
      () => store.ready,
      (ready) => {
        if (ready) resolve()
      }
    )
  })
}

// --- Token cache ---

// A live access token survives a reload in sessionStorage (tab-scoped, gone
// with the tab), keyed by user so a different sign-in in the same tab never
// picks it up. Google tokens live an hour; reusing one skips the google_token
// round trip on page load.
const TOKEN_CACHE_KEY = "auger.google.token"

function readCachedToken(userId: string | null) {
  if (!userId) return null
  try {
    const cached = JSON.parse(sessionStorage.getItem(TOKEN_CACHE_KEY) || "null")
    return cached?.userId === userId && isTokenActive(cached.token) ? cached.token : null
  } catch {
    return null
  }
}

function writeCachedToken(userId: string | null, token: GoogleToken) {
  if (!userId) return
  try {
    sessionStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify({ userId, token }))
  } catch {
    // storage unavailable (private mode, quota) — the next load fetches again
  }
}

function clearCachedToken() {
  try {
    sessionStorage.removeItem(TOKEN_CACHE_KEY)
  } catch {
    // storage unavailable — nothing cached
  }
}

// --- Token management ---

function setAccessToken(tokenInfo: GoogleToken) {
  const store = useGoogleStore()
  store.setToken(tokenInfo)
  store.setAuthorizationNeeded(false)
  writeCachedToken(useUserStore().principal?.identity.stytch_member_id ?? null, tokenInfo)
}

// Resolves to the user's access token, or null when Google needs
// authorizing or the request failed. Needs no Google script, so it can run
// while the libraries are still loading.
async function requestAccessToken(): Promise<GoogleToken | null> {
  await getUserEmail()
  const cached = readCachedToken(useUserStore().principal?.identity.stytch_member_id ?? null)
  if (cached) return cached
  try {
    const response = await netlifyFunctionClient().get<GoogleTokenResponse>("google_token")
    const token = response.data?.token
    const status = response.data?.status
    if (token) return token
    if (status === "authorization_needed") {
      clearCachedToken()
      useGoogleStore().setAuthorizationNeeded(true)
    } else {
      console.error("Unexpected google token response", response)
    }
    return null
  } catch (err) {
    console.error("Failed to fetch google token:", err)
    return null
  }
}

async function fetchAccessToken() {
  const token = await requestAccessToken()
  if (token) setAccessToken(token)
}

async function refreshToken() {
  const store = useGoogleStore()
  if (store.authorizationNeeded) return false
  await fetchAccessToken()
  return isTokenActive(store.token)
}

// The token every Google request carries, asked for as the request goes out.
// It waits until the session is ready and refreshes a token that is near expiry.
// After a 401 the package asks again with `refresh`, which skips the
// sessionStorage copy so the server is asked for a fresh token. Null means
// Google is not connected for this user.
async function currentToken({ refresh = false } = {}): Promise<string | null> {
  try {
    await ensureReady()
    const store = useGoogleStore()
    if (refresh) clearCachedToken()
    else if (isTokenActive(store.token)) return store.token?.token ?? null
    return (await refreshToken()) ? (store.token?.token ?? null) : null
  } catch (err) {
    console.warn("Google token unavailable:", err)
    return null
  }
}

/** The Calendar and Gmail client for the signed-in user. It keeps its own token
 *  fresh, so it can be handed to composables as a plain dependency. */
const google = createGoogle({ token: currentToken })

// --- Script loading ---

function loadGIS() {
  return new Promise<GoogleAccounts>((resolve) => {
    if (scripts.loadedGIS) return resolve(window.google.accounts)
    if (scripts.loadingGIS) return // already in progress

    scripts.loadingGIS = true
    const scriptEl = document.createElement("script")
    scriptEl.addEventListener("load", () => {
      scripts.loadedGIS = true
      scripts.loadingGIS = false
      resolve(window.google.accounts)
    })
    scriptEl.src = "https://accounts.google.com/gsi/client"
    scriptEl.async = true
    scriptEl.defer = true
    document.head.appendChild(scriptEl)
  })
}

// --- Authorization ---

let codeClient: CodeClient | null = null

// --- Public API ---

/**
 * Initialize Google libraries and fetch access token.
 * Called once from App.vue at startup.
 */
export async function initGoogle() {
  const store = useGoogleStore()
  const loadLibraries = async () => {
    await loadGIS()
    store.libraryLoadingComplete()
  }
  // The token request runs alongside the library load.
  const [, token] = await Promise.all([loadLibraries(), requestAccessToken()])
  if (token) setAccessToken(token)
  store.setReady(true)
}

/**
 * Composable for Google auth state and client access.
 *
 * Provides reactive auth state and methods for authorization,
 * revocation, and getting an authorized Google client.
 *
 * @param {Object} deps - Required dependencies
 * @param {Object} deps.client - Apollo client
 * @param {Object} deps.toast - Toast notification instance
 */
export function useGoogle(deps: ComposableDeps) {
  const { client, toast } = deps
  const store = useGoogleStore()

  // --- Computed state ---
  const available = computed(() => store.googleAvailable)
  const authorizationNeeded = computed(() => store.authorizationNeeded)
  const ready = computed(() => store.ready)
  const canModifyGmail = computed(() =>
    (store.token?.scope ?? "").split(" ").includes(GMAIL_MODIFY_SCOPE)
  )

  // --- Authorization callbacks ---

  async function handleCodeRequest(response: CodeResponse) {
    const body = { code: response.code, scope: response.scope }
    try {
      const result = await netlifyFunctionClient().post<CompleteAuthorizationResponse>(
        "complete_authorization",
        body
      )
      const token = result.data?.token
      if (token) {
        setAccessToken(token)
      } else {
        console.error("No token returned from authorization", result)
        toast?.error?.("Google authorization failed — no token returned")
      }
    } catch (err) {
      const message = httpErrorMessage(err) || "Google authorization failed"
      console.error("Authorization completion failed:", err)
      toast?.error?.(message)
    }
  }

  function handleCodeRequestError(err: unknown) {
    console.error("Google authorization failed", err)
    toast?.error?.("Google authorization failed")
  }

  async function initCodeClient() {
    const scopes = [...SCOPES.calendar, ...SCOPES.gmail, ...SCOPES.connectedApps]
    const email = await getUserEmail()
    const accounts = await ensureLibrariesLoaded()
    return (codeClient = accounts.oauth2.initCodeClient({
      client_id: googleConfig.clientId,
      scope: scopes.join(" "),
      ux_mode: "popup",
      hint: email,
      hosted_domain: "heavybit.com",
      callback: handleCodeRequest,
      error_callback: handleCodeRequestError,
    }))
  }

  // --- Methods ---

  /**
   * Open OAuth2 popup to authorize Google access.
   */
  async function authorize() {
    try {
      const client = codeClient ?? (await initCodeClient())
      client.requestCode()
    } catch (err) {
      toast?.error?.("Google authorization failed")
      throw err
    }
  }

  /**
   * Revoke Google access and clean up stored tokens: revoke a live token with
   * Google itself, then delete the membership's token rows through core (the
   * member's own staff delete permission). The rows go even with no live
   * token: an expired access token or a refresh token can still be stored.
   * Without a person id there are no rows to address.
   */
  async function revoke() {
    try {
      const token = await currentToken()
      if (token) {
        const accounts = await ensureLibrariesLoaded()
        accounts.oauth2.revoke(token)
      }
      const personId = useUserStore().principal?.identity.auger_person_id
      if (typeof personId === "number") {
        const { accessTokens, refreshTokens } = await getAuger(client).google.revoke({
          personId,
        })
        if (accessTokens === 0 && refreshTokens === 0) {
          console.warn("google revoke matched no rows", { personId })
        }
      }
      clearCachedToken()
      store.resetGoogle()
      toast?.success?.("Google access revoked")
    } catch (err) {
      toast?.error?.("Failed to revoke Google access")
      throw err
    }
  }

  return {
    available,
    authorizationNeeded,
    ready,
    canModifyGmail,
    google,
    authorize,
    revoke,
  }
}
